import 'react-datepicker/dist/react-datepicker-cssmodules.css';
import 'react-datepicker/dist/react-datepicker.css';

import {
  Box,
  Button,
  Checkbox,
  CheckboxProps,
  HStack,
  Input,
  InputProps,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  NumberDecrementStepper,
  NumberDecrementStepperProps,
  NumberIncrementStepper,
  NumberIncrementStepperProps,
  NumberInput,
  NumberInputField,
  NumberInputFieldProps,
  NumberInputProps,
  NumberInputStepper,
  NumberInputStepperProps,
  Radio,
  RadioGroup,
  RadioGroupProps,
  Select,
  SelectProps,
  Stack,
  StackProps,
  Tag,
  Text,
  Textarea,
  TextareaProps,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import React, { PropsWithChildren } from 'react';
import { ReactDatePickerProps } from 'react-datepicker';
import {
  FieldArrayPath,
  FieldValues,
  Path,
  useForm,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';

import { TypeUtils } from '../../framework/utils/UtilityTypes';
import { AutoResizeTextarea } from '../components/AutoResizeTextarea';
import DatePicker from '../components/DatePicker';
import FormItem, {
  FormItemMultiple,
  FormItemMultipleProps,
  FormItemProps,
  ItemProps,
} from '../components/FormItem';
import { Formatter } from '../utils/FormatUtils';

type ExtendedFormItemProps<
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>
> = Omit<FormItemProps<S, N>, 'control'>;

type ExtendedFormItemMultipleProps<
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>
> = Omit<FormItemMultipleProps<S, N>, 'control'>;

export type ExtendedFormArrayProps<
  S extends FieldValues = FieldValues,
  N extends FieldArrayPath<S> = FieldArrayPath<S>
> = Omit<FormItemProps<S, N>, 'control'>;

type FormCheckboxProps<
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>
> = PropsWithChildren<
  ExtendedFormItemProps<S, N> & {
    checkboxProps?: Partial<CheckboxProps>;
  }
>;

type FormDatePickerProps<
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>
> = ExtendedFormItemProps<S, N> & {
  datePickerProps?: Partial<ReactDatePickerProps>;
  onClickClear: Consumer<N>;
};

type FormDateRangeProps<
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>
> = ExtendedFormItemMultipleProps<S, N> & {
  datePickerProps?: Partial<ReactDatePickerProps>;
  onClickClear: Consumer<N>;
};

type FormInputProps<
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>
> = ExtendedFormItemProps<S, N> & {
  inputProps?: Partial<InputProps>;
};

type FormInputNumberProps<
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>
> = ExtendedFormItemProps<S, N> & {
  numberDecrementStepperProps?: Partial<NumberDecrementStepperProps>;
  numberIncrementStepperProps?: Partial<NumberIncrementStepperProps>;
  numberInputProps?: Partial<NumberInputProps>;
  numberInputFieldProps?: Partial<NumberInputFieldProps>;
  numberInputStepperProps?: Partial<NumberInputStepperProps>;
};

type SelectOption<V> = {
  value: V;
  valueToDisplay?: React.ReactNode;
};
type FormMultiSelectProps<
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>,
  V = string | number
> = ExtendedFormItemProps<S, N> & {
  disabled?: boolean;
  options: SelectOption<V>[];
  valueConverter?: FunctionType<string, V | null>;
};

type FormRadioGroupProps<
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>,
  V = string | number
> = PropsWithChildren<
  ExtendedFormItemProps<S, N> & {
    options: SelectOption<V>[];
    radioGroupProps?: Partial<RadioGroupProps>;
    stackProps?: Partial<StackProps>;
    valueConverter?: FunctionType<string, V | null>;
  }
>;

export type FormSelectProps<
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>,
  V = string | number
> = ExtendedFormItemProps<S, N> & {
  options: SelectOption<V>[];
  selectProps?: Partial<SelectProps>;
  valueConverter?: FunctionType<string, V | null>;
};

type FormTextareaProps<
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>
> = ExtendedFormItemProps<S, N> & {
  textareaProps?: Partial<TextareaProps>;
};

type UseFormItemsReturn<
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>
> = {
  FormCheckbox: React.FunctionComponent<FormCheckboxProps<S, N>>;
  FormDatePicker: React.FunctionComponent<FormDatePickerProps<S, N>>;
  FormDateRange: React.FunctionComponent<FormDateRangeProps<S, N>>;
  FormInput: React.FunctionComponent<FormInputProps<S, N>>;
  FormInputNumber: React.FunctionComponent<FormInputNumberProps<S, N>>;
  FormItem: React.FunctionComponent<FormItemProps<S, N>>;
  FormMultiSelect: <V = string | number>(
    props: FormMultiSelectProps<S, N, V>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context?: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => React.ReactElement<any, any> | null;
  FormRadioGroup: <V = string | number>(
    props: FormRadioGroupProps<S, N, V>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context?: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => React.ReactElement<any, any> | null;
  FormSelect: <V = string | number>(
    props: FormSelectProps<S, N, V>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context?: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => React.ReactElement<any, any> | null;
  FormText: React.FunctionComponent<ExtendedFormItemProps<S, N>>;
  FormTextarea: React.FunctionComponent<FormTextareaProps<S, N>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<S, any>;
};

/**
 * useFormItems インターフェース
 */
interface UseFormItems {
  <S extends FieldValues = FieldValues, N extends Path<S> = Path<S>>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    defaultForm?: UseFormReturn<S, any>
  ): UseFormItemsReturn<S, N>;
  <S extends FieldValues = FieldValues, N extends Path<S> = Path<S>>(
    props?: UseFormProps<S, N>
  ): UseFormItemsReturn<S, N>;
}

/**
 * useForm プロパティであることを検証するタイプガード
 * @param props - プロパティ
 * @returns
 */
const isFormProps = <
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>
>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: UseFormProps<S, N> | UseFormReturn<S, any>
): props is UseFormProps<S, N> => !('control' in props);

/**
 * フォームアイテムフック
 * @param formProps - useForm プロパティないし既存のフォームオブジェクト
 * @returns フォームコンポーネントおよびフォームオブジェクト
 */
export const useFormItems: UseFormItems = <
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>
>(
  formProps?: UseFormProps<S> | UseFormReturn<S>
) => {
  let form: UseFormReturn<S>;
  if (TypeUtils.isNil(formProps)) {
    form = useForm<S>();
  } else if (isFormProps(formProps)) {
    form = useForm<S>(formProps);
  } else {
    form = formProps;
  }

  /**
   * チェックボックス
   * @param props -
   * @returns
   */
  const FormCheckbox: React.FunctionComponent<FormCheckboxProps<S, N>> = ({
    checkboxProps,
    children,
    ...props
  }) => (
    <FormItem
      control={form.control}
      renderNode={({ field: { onChange, ...field } }) => (
        <Checkbox
          {...checkboxProps}
          {...field}
          onChange={(event) => {
            onChange(event);
            if (!TypeUtils.isNil(checkboxProps?.onChange)) {
              checkboxProps?.onChange(event);
            }
          }}
        >
          {children}
        </Checkbox>
      )}
      viewNode={({ field }) => (
        <Checkbox {...checkboxProps} {...field} isReadOnly>
          {children}
        </Checkbox>
      )}
      {...props}
    />
  );

  const FormDatePicker: React.FunctionComponent<FormDatePickerProps<S, N>> = ({
    datePickerProps,
    onClickClear,
    ...props
  }) => (
    <FormItem
      control={form.control}
      renderNode={({ field }) => (
        <DatePicker
          datePickerProps={datePickerProps}
          field={field}
          onClickClear={() => onClickClear(props.name)}
        />
      )}
      value={(field) =>
        TypeUtils.isNilString(field.value)
          ? '―'
          : format(
              Formatter.createDate(field.value),
              datePickerProps?.showTimeInput || datePickerProps?.showTimeSelect
                ? Formatter.format.DATETIME
                : Formatter.format.DATE
            )
      }
      {...props}
    />
  );

  const FormDateRange: React.FunctionComponent<FormDateRangeProps<S, N>> = ({
    datePickerProps,
    onClickClear,
    ...props
  }) => (
    <FormItemMultiple
      control={form.control}
      delimiter="～"
      {...props}
      forms={props.forms.map(
        (form) =>
          ({
            ...form,
            renderNode: ({ field }) => (
              <DatePicker
                datePickerProps={datePickerProps}
                field={field}
                onClickClear={() => onClickClear(form.name)}
              />
            ),
          } as ItemProps<S, N>)
      )}
    />
  );

  const FormInput: React.FunctionComponent<FormInputProps<S, N>> = ({
    inputProps,
    ...props
  }) => (
    <FormItem
      control={form.control}
      renderNode={({ field: { onChange, ...field } }) => (
        <Input
          {...inputProps}
          {...field}
          onChange={(event) => {
            onChange(event);
            if (!TypeUtils.isNil(inputProps?.onChange)) {
              inputProps?.onChange(event);
            }
          }}
        />
      )}
      {...props}
    />
  );

  /**
   * バリデーションするときはtoStringして文字列として見たほうがいいかも
   *
   * @param param0 -
   * @returns
   */
  const FormInputNumber: React.FunctionComponent<
    FormInputNumberProps<S, N>
  > = ({
    numberDecrementStepperProps,
    numberIncrementStepperProps,
    numberInputProps,
    numberInputFieldProps,
    numberInputStepperProps,
    ...props
  }) => (
    <FormItem
      control={form.control}
      renderNode={({ field: { onChange, ...field } }) => (
        <NumberInput
          {...numberInputProps}
          {...field}
          onChange={(valueAsString, valueAsNumber) => {
            onChange(valueAsString, valueAsNumber);
            if (!TypeUtils.isNil(numberInputProps?.onChange)) {
              numberInputProps?.onChange(valueAsString, valueAsNumber);
            }
          }}
        >
          <NumberInputField {...numberInputFieldProps} />
          <NumberInputStepper {...numberInputStepperProps}>
            <NumberIncrementStepper {...numberIncrementStepperProps} />
            <NumberDecrementStepper {...numberDecrementStepperProps} />
          </NumberInputStepper>
        </NumberInput>
      )}
      viewNode={({ field }) => (
        // <NumberInput
        //   {...numberInputProps}
        //   {...field}
        //   value={
        //     TypeUtils.isNil(props.value)
        //       ? field.value
        //       : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        //         (props.value(field) as any)
        //   }
        //   isReadOnly
        // >
        //   <NumberInputField {...numberInputFieldProps} />
        // </NumberInput>
        <Text>
          {TypeUtils.isNil(props.value)
            ? field.value
            : // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (props.value(field) as any)}
        </Text>
      )}
      {...props}
    />
  );

  const FormMultiSelect = <V = string | number,>({
    disabled,
    options,
    valueConverter,
    ...props
  }: FormMultiSelectProps<S, N, V>) => (
    <FormItem
      control={form.control}
      renderNode={({ field: { onChange, value, ...field } }) => (
        <Menu closeOnSelect={false}>
          <HStack alignItems="flex-end">
            <Box
              border="1px solid"
              borderColor="gray.400"
              display="flex"
              flexWrap="wrap"
              height="full"
              minHeight="40px"
              rounded="md"
              width="calc(100% - 76px)"
            >
              {Array.isArray(value) ? (
                value.map((v: string, idx: number) => {
                  return (
                    <Tag key={idx} margin="1">
                      {options.find(
                        ({ value: opVal }) => v.toString() === String(opVal)
                      )?.valueToDisplay ||
                        v ||
                        '―'}
                    </Tag>
                  );
                })
              ) : value !== undefined ? (
                <Tag margin="1">{value}</Tag>
              ) : (
                '　'
              )}
            </Box>
            <MenuButton as={Button} width="" bottom={0} isDisabled={disabled}>
              Select
            </MenuButton>
          </HStack>
          <MenuList maxH={'16rem'} css={{ overflowY: 'auto' }}>
            <MenuOptionGroup
              type="checkbox"
              {...field}
              onChange={
                TypeUtils.isNil(valueConverter)
                  ? onChange
                  : (value) =>
                      form.setValue(
                        props.name,
                        (Array.isArray(value)
                          ? value.map(valueConverter)
                          : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            valueConverter(value)) as any
                      )
              }
              value={
                Array.isArray(value)
                  ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    value.map((v: any) => v.toString())
                  : value?.toString()
              }
            >
              {options.map(({ value, valueToDisplay }, idx) => (
                <MenuItemOption key={idx} value={String(value)}>
                  {valueToDisplay || String(value)}
                </MenuItemOption>
              ))}
            </MenuOptionGroup>
          </MenuList>
        </Menu>
      )}
      value={({ value }) =>
        Array.isArray(value) ? (
          value.map((v: string, idx: number) => (
            <Tag key={idx} margin="1">
              {options.find(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ({ value: opVal }) => v.toString() === (opVal as any).toString()
              )?.valueToDisplay ||
                v ||
                '―'}
            </Tag>
          ))
        ) : value !== undefined ? (
          <Tag margin="1">{value}</Tag>
        ) : (
          '　'
        )
      }
      {...props}
    />
  );

  /**
   * ラジオボタングループ
   * @param props -
   * @returns
   */
  const FormRadioGroup = <V = string | number,>({
    children,
    options,
    radioGroupProps,
    stackProps,
    valueConverter,
    ...props
  }: FormRadioGroupProps<S, N, V>) => (
    <FormItem
      control={form.control}
      renderNode={({ field: { onChange, value, ...field } }) => (
        <RadioGroup
          {...radioGroupProps}
          defaultValue={radioGroupProps?.defaultValue?.toString()}
          onChange={(nextValue) => {
            TypeUtils.isNil(valueConverter)
              ? onChange(nextValue)
              : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                form.setValue(props.name, valueConverter(nextValue) as any);
            if (!TypeUtils.isNil(radioGroupProps?.onChange)) {
              radioGroupProps?.onChange(nextValue);
            }
          }}
          value={value?.toString()}
          {...field}
        >
          {children}
          <Stack
            direction="column"
            {...stackProps}
            hidden={!TypeUtils.isNil(children)}
          >
            {options?.map(({ value, valueToDisplay }, index) => (
              <Radio key={index} value={String(value)}>
                {valueToDisplay || String(value)}
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      )}
      value={(field) =>
        options.find(({ value }) => field.value === value)?.valueToDisplay ||
        field.value ||
        '―'
      }
      {...props}
    />
  );

  /**
   * セレクト
   * @param param0 -
   * @returns
   */
  const FormSelect = <V = string | number,>({
    options,
    selectProps,
    valueConverter,
    ...props
  }: FormSelectProps<S, N, V>) => (
    <FormItem
      control={form.control}
      renderNode={({ field: { onChange, value, ...field } }) => (
        <Select
          placeholder="　"
          {...selectProps}
          defaultValue={selectProps?.defaultValue?.toString()}
          onChange={(e) => {
            if (TypeUtils.isNil(valueConverter)) {
              onChange(e);
            } else {
              const v = valueConverter(e.target.value);
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              form.setValue(props.name, v as any);
            }
            if (!TypeUtils.isNil(selectProps?.onChange)) {
              selectProps?.onChange(e);
            }
          }}
          value={value?.toString()}
          {...field}
        >
          {options?.map(({ value, valueToDisplay }, index) => (
            <option key={index} value={String(value)}>
              {valueToDisplay || String(value)}
            </option>
          ))}
        </Select>
      )}
      value={(field) =>
        options.find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ({ value }) => field.value?.toString() === (value as any)?.toString()
        )?.valueToDisplay ||
        field.value ||
        '―'
      }
      {...props}
    />
  );

  /**
   * フォームと同じ形式でのデータ表示用
   * @param props -
   * @returns
   */
  const FormText: React.FunctionComponent<ExtendedFormItemProps<S, N>> = (
    props
  ) => <FormItem control={form.control} readonly {...props} />;

  /**
   * テキストエリア
   * @param props -
   * @returns
   */
  const FormTextarea: React.FunctionComponent<FormTextareaProps<S, N>> = ({
    textareaProps,
    ...props
  }) => (
    <FormItem
      control={form.control}
      renderNode={({ field: { onChange, ...field } }) => (
        <Textarea
          {...textareaProps}
          {...field}
          onChange={(event) => {
            onChange(event);
            if (!TypeUtils.isNil(textareaProps?.onChange)) {
              textareaProps?.onChange(event);
            }
          }}
        >
          {field.value}
        </Textarea>
      )}
      viewNode={({ field: { onChange, ...field } }) => (
        <Box as={AutoResizeTextarea}>{field.value}</Box>
      )}
      {...props}
    />
  );

  return {
    FormCheckbox,
    FormDatePicker,
    FormDateRange,
    FormRadioGroup,
    FormInput,
    FormInputNumber,
    FormItem,
    FormMultiSelect,
    FormSelect,
    FormText,
    FormTextarea,
    form,
  };
};
